import React, {useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';
import {t} from 'i18next';
import {useDispatch, useSelector} from 'react-redux';
import dayjs from 'dayjs';
import {Radio} from 'antd';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {light, regular} from '@fortawesome/fontawesome-svg-core/import.macro';
import {Colors} from '@arboxappv4/shared/src/styles/Colors';
import {DB_DATE_FORMAT} from '@arboxappv4/shared/src/constants/constants';
import {getPayForSlotMembershipType} from '@arboxappv4/shared/src/helpers/functions';
import {membershipTypes} from '@arboxappv4/shared/src/helpers/constants';
import {
    openModal,
    updateModalRes,
    WAITLIST_DONT_WANT_SLOT_MODAL
} from '@arboxappv4/shared/src/redux/modalManagerSlice';
import {setOfferInvalidReason} from '@arboxappv4/shared/src/redux/kioskStandAlone/kioskSlice';
import {Btn, ErrorText} from '../../../styles/globalStyles';
import UserAvatar from '../../../components/UserAvatar';
import CountdownTimer from '../../../components/CountdownTimer';
import SummaryCard from '../../../components/SummaryCard';
import useCountdownTimer from '../../../helpers/hooks/useCountdownTimer';
import useScreenSize from '../../../helpers/hooks/useScreenSize';
import {handlePricingWithCurrency} from '../../../helpers/functions';
import {
    appointmentWaitlistRejections,
    getWaitlistOfferLabels,
    translateSlotOfferToScheduleObj
} from "./WaitlistHelpers";
import {updateFlags} from "@arboxappv4/shared/src/redux/stepperSlice";


const AppointmentWaitlistOffer = (props) => {
    const {values, setFieldValue, handleSubmit, errors, touched} = props;
    const dispatch = useDispatch();
    const {isMobile} = useScreenSize();
    const offer = useSelector(state => state.kiosk.appointmentWaitlistOffer);
    const selectedLocation = useSelector(state => state.kiosk.selectedLocation);
    const language = useSelector(state => state.kiosk.language);
    const memberships = useSelector(state => state.kiosk.memberships);
    const dontWantRes = useSelector(state => state.modalManager[WAITLIST_DONT_WANT_SLOT_MODAL].processRes);
    const timer = useSelector(state => state.stepper.timer);
    const [submitted, setSubmitted] = useState(false);
    const [confirmed, setConfirmed] = useState(false);

    const payForSlot = useMemo(() => {
        if (!values?.schedule) return null;
        return getPayForSlotMembershipType(values?.schedule);
    }, [values?.schedule]);

    const currencySymbol = useMemo(() => selectedLocation?.currency_symbol ?? '', [selectedLocation]);
    const {serviceName, coach, dateLabel, timeLabel} = useMemo(() => getWaitlistOfferLabels(offer), [offer, selectedLocation, language]);

    const selectedPaymentLabel = useMemo(() => {
        if(values?.selectedMembership) {
            if (values?.selectedMembership.payForSlot) return t('waitlist-pfs-selected-summary-label')
            if (values?.selectedMembership.mt_type === membershipTypes.PLAN) return t('waitlist-plan-selected-summary-label')
            if (values?.selectedMembership.mt_type === membershipTypes.PUNCH_CARD) return t('waitlist-punch-selected-summary-label')
        }
    }, [values?.selectedMembership, currencySymbol]);

    useEffect(() => {
        if (dontWantRes) {
            dispatch(updateFlags({payForSlot: false}))
            setFieldValue('declineAction', dontWantRes)
            dispatch(updateModalRes({modalName: WAITLIST_DONT_WANT_SLOT_MODAL, res: null}));
            setConfirmed(true)
        }
    }, [dontWantRes, dispatch]);

    useEffect(() => {
        console.log('values', values)
        console.log('errors', errors)
    }, [values, errors]);

    useEffect(() => {
        if(offer) {
            if(!values.locations_box_fk) setFieldValue('locations_box_fk', offer.locations_box_id)
            if(!values.schedule) setFieldValue('schedule', translateSlotOfferToScheduleObj())
        }
    }, [offer, values?.schedule, values?.locations_box_fk]);

    useEffect(() => {
        if(confirmed && !submitted) {
            handleSubmit()
            setSubmitted(true)
        }
    }, [confirmed, submitted]);

    const selectMembership = (membership, isPayForSlot) => {
        dispatch(updateFlags({payForSlot: isPayForSlot}))
        setFieldValue('declineAction', null)
        setFieldValue('id', membership.id);
        setFieldValue('payForSlot', isPayForSlot);
        setFieldValue('selectedMembership', {...membership, payForSlot: isPayForSlot});
    };

    const onConfirmBtnClick = () => {
        setFieldValue('declineAction', null)
        setConfirmed(true)
    }

    const getMembershipInfo = (membership) => {
        let info = {text: '', badge: false}
        if(isMobile) return info
        if (membership.type === membershipTypes.PAY_FOR_SLOT) {
            return {text: handlePricingWithCurrency(membership.price, currencySymbol), badge: false, selectedText: t('pay-for-slot-selected-explain-waitlist')};
        }
        if (membership.mt_type === membershipTypes.PLAN) {
            if(membership.active === 2) return {text: `${t('future-start-date')} ${dayjs(membership.start, DB_DATE_FORMAT).format(selectedLocation?.date_format)}`, badge: true}
            return {text: membership.end ? `${t('membership-end-date')} ${dayjs(membership.end, DB_DATE_FORMAT).format(selectedLocation?.date_format)}` : t('no-end-date'), badge: true,};
        }
        if (membership.mt_type === membershipTypes.PUNCH_CARD) {
            return {
                text: t('left-sessions', {num: membership.sessions_left}),
                badge: true, danger: membership.sessions_left <= 0,
                selectedText: t('punch-selected-explain-waitlist', {total: membership.membership_types.sessions, remain: (membership.sessions_left - 1)})
            };
        }
        return info;
    };

    const paymentOption = ({key, selected, onClick, label, info}) => (
        <PayRow key={key} onClick={onClick} style={{borderColor: selected ? Colors.siteBlack : Colors.siteScheduleBorder, background: selected ? Colors.siteBg : Colors.white,}}>
            <Row gap={'10px'} justify={'space-between'} style={{width: '100%'}}>
                <Row gap={'10px'} flex={'1 1 auto'}>
                    <Radio checked={selected}/>
                    <Text weight={600}>{label}</Text>
                </Row>
                {!!info.text && info.badge ? <Badge danger={!!info.danger}>{info.text}</Badge> : <Text weight={600} shrink>{info.text}</Text>}
            </Row>
            {selected && info.selectedText &&
                <Text size={14} weight={500} color={Colors.grey} style={{paddingInlineStart: 26}}>{info.selectedText}</Text>
            }
        </PayRow>
    );

    const metaItem = (icon, text) => (
        <Row gap={'6px'}>
            <FontAwesomeIcon icon={icon} color={Colors.greyText} style={{fontSize: 12}}/>
            <Text color={Colors.greyText}>{text}</Text>
        </Row>
    );

    const banner = (
        <Banner mobile={isMobile}>
            <FontAwesomeIcon icon={regular('bolt')} color={Colors.purpleIcon} style={{fontSize: 15, marginTop: isMobile ? 5 : 0, flexShrink: 0}}/>
            <Text style={{flex: 1, lineHeight: '22px', alignSelf: 'center'}}>{t(`appointment-waitlist-offer-banner${isMobile ? '-mobile' : ''}`)}</Text>
        </Banner>
    );

    const countdownTimer = (
        <CountdownTimer labelExpired={t('appointment-waitlist-offer-timer-expired')}
                        labelUrgent={t('appointment-waitlist-offer-timer-urgent')}
                        timerLabel={t('appointment-waitlist-offer-timer-label')}
                        onExpire={() => dispatch(setOfferInvalidReason(appointmentWaitlistRejections.CONFIRM_WINDOW_EXPIRED))}
        />
    );

    const slotCard = (
        <OfferCard>
            <CardHeader>{t('appointment-waitlist-offer-slot-label')}</CardHeader>
            <Col gap={'8px'} padding={isMobile ? '16px 20px' : '20px 24px'}>
                <Text size={19} weight={600}>{serviceName}</Text>
                <Row gap={'8px 16px'} wrap>
                    {coach && <UserAvatar user={coach} nameType={'full'} size={20} fontSize={'9px'}/>}
                    {dateLabel && metaItem(light('calendar'), dateLabel)}
                    {timeLabel && metaItem(light('clock'), timeLabel)}
                </Row>
            </Col>
        </OfferCard>
    );

    // desktop: fillHeight so only the options list scrolls; mobile: natural height, page scrolls
    const paymentCard = (
        <OfferCard fillHeight={!isMobile} style={{marginBottom: isMobile ? 0 : '16px'}}>
            <Col gap={'2px'} padding={isMobile ? '16px 20px 17px' : '16px 24px 17px'} style={{borderBottom: '1px solid #f0f0f0'}}>
                <Text size={16} weight={600}>{t('appointment-waitlist-offer-payment-heading')}</Text>
                <Text size={14} color={Colors.greyText}>{t('appointment-waitlist-offer-payment-desc')}</Text>
            </Col>
            <Col gap={'10px'} padding={'16px 20px'} overflow={isMobile ? undefined : 'auto'} flex={isMobile ? undefined : '1 1 auto'}>
                {payForSlot && paymentOption({
                    selected: values.id === payForSlot.id && values.payForSlot === true,
                    onClick: () => selectMembership(payForSlot, true),
                    label: t('pay-for-type', {type: payForSlot.name, interpolation: {escapeValue: false}}),
                    info: getMembershipInfo(payForSlot),
                })}
                {memberships?.map(membership => paymentOption({
                    key: `offer-mu-${membership.id}`,
                    selected: values.id === membership.id && values.payForSlot === false,
                    onClick: () => selectMembership(membership, false),
                    label: membership.membership_types?.name,
                    info: getMembershipInfo(membership),
                }))}
                {!payForSlot && !memberships?.length &&
                    <Col padding={'30px 16px'} style={{alignItems: 'center'}}>
                        <EmptyCircle>
                            <FontAwesomeIcon icon={light('heart-crack')} color={Colors.greyText} style={{fontSize: 16}}/>
                        </EmptyCircle>
                        <Text weight={500} color={Colors.greyText} style={{marginTop: 12, textAlign: 'center'}}>{t('appointment-waitlist-offer-no-payment')}</Text>
                        <Text size={14} color={'#b0b0b0'} style={{marginTop: 4, textAlign: 'center'}}>{t('appointment-waitlist-offer-no-payment-desc')}</Text>
                    </Col>
                }
                {touched.id && errors.id && <ErrorText>{errors.id}</ErrorText>}
            </Col>
        </OfferCard>
    );

    const getSummaryRows = () => {
        const rows = [
            {label: t('appointment-waitlist-summary-service'), value: serviceName || '—'},
            {label: t('appointment-waitlist-summary-coach'), value: coach?.full_name || '—'},
            {label: t('appointment-waitlist-summary-date'), value: dateLabel || '—'},
            {label: t('appointment-waitlist-summary-time'), value: timeLabel || '—'},
        ];
        if (selectedPaymentLabel) {
            rows.push({label: t('appointment-waitlist-offer-summary-payment'), value: selectedPaymentLabel, separated: true});
        }
        if (values?.selectedMembership?.payForSlot) {
            rows.push({label: t('total'), value: handlePricingWithCurrency(values?.selectedMembership.price, currencySymbol), bold: true});
        }
        return rows;
    };

    const summaryCard = (footer) => <SummaryCard title={t('appointment-waitlist-offer-summary-title')} rows={getSummaryRows()} footer={footer}/>

    const actions = (padded) => (
        <Col gap={'8px'} padding={padded ? '0 20px 20px' : '0'}>
            <Btn type={'primary'} width={'100%'} onClick={onConfirmBtnClick} disabled={!timer || !values.id} className={'site-primary-btn'}>
                <Row gap={'8px'} justify={'center'} style={{display: 'inline-flex'}}>
                    <FontAwesomeIcon icon={light('check')}/>
                    <span>{t(`appointment-waitlist-offer-confirm${values.payForSlot ? '-pfs' : ''}`)}</span>
                </Row>
            </Btn>
            <Text weight={500} style={{textAlign: 'center', textDecoration: 'underline', cursor: 'pointer'}} onClick={() => dispatch(openModal({modalName: WAITLIST_DONT_WANT_SLOT_MODAL}))}>
                {t('appointment-waitlist-offer-dont-want')}
            </Text>
        </Col>
    );

    if (!offer) return null;

    if (isMobile) {
        return (
            <MobileRoot>
                <Col gap={'16px'}>
                    {banner}
                    {countdownTimer}
                </Col>
                <MobileScroll>
                    {slotCard}
                    {paymentCard}
                    {summaryCard(null)}
                    {actions(false)}
                </MobileScroll>
            </MobileRoot>
        );
    }

    return (
        <Col gap={'16px'} height={'100%'} overflow={'hidden'} style={{width: '100%'}}>
            {banner}
            <Row gap={'24px'} align={'flex-start'} height={'calc(100% - 50px)'} style={{width: '100%'}}>
                <Col gap={'16px'} flex={'1 1 auto'} height={'100%'} overflow={'hidden'}>
                    {slotCard}
                    {paymentCard}
                </Col>
                <Col gap={'16px'} flex={'0 0 340px'}>
                    {countdownTimer}
                    {summaryCard(actions(true))}
                </Col>
            </Row>
        </Col>
    );
};

export default AppointmentWaitlistOffer;

/* Local primitives with safe defaults (flex: 0 0 auto, overflow visible) —
   the global flex/card components default to flex:1 + overflow:hidden and break scroll here */

const Col = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({gap}) => gap || 0};
    padding: ${({padding}) => padding || 0};
    flex: ${({flex}) => flex || '0 0 auto'};
    height: ${({height}) => height || 'auto'};
    overflow: ${({overflow}) => overflow || 'visible'};
    min-width: 0;
    min-height: 0;
    box-sizing: border-box;
`;

const Row = styled.div`
    display: flex;
    align-items: ${({align}) => align || 'center'};
    justify-content: ${({justify}) => justify || 'flex-start'};
    gap: ${({gap}) => gap || 0};
    flex: ${({flex}) => flex || '0 0 auto'};
    flex-wrap: ${({wrap}) => wrap ? 'wrap' : 'nowrap'};
    height: ${({height}) => height || 'auto'};
    min-width: 0;
    min-height: 0;
    box-sizing: border-box;
`;

const Text = styled.div`
    font-size: ${({size}) => size || 15}px;
    font-weight: ${({weight}) => weight || 400};
    color: ${({color}) => color || Colors.siteBlack};
    flex-shrink: ${({shrink}) => shrink ? 0 : 1};
    min-width: 0;
    word-break: break-word;
`;

const MobileRoot = styled(Col)`
    width: 100%;
    max-width: 100%;
    height: 100%;
    max-height: calc(100dvh - 125px);
    overflow: hidden;
    padding: 0 16px 16px;
    gap: 16px;
`;

const MobileScroll = styled(Col)`
    flex: 1 1 0;
    gap: 16px;
    padding-bottom: 8px;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
`;

const Banner = styled(Row)`
    align-items: ${({mobile}) => mobile ? 'flex-start' : 'center'};
    gap: 10px;
    min-height: 48px;
    padding: 10px 16px;
    border-radius: 10px;
    background: ${Colors.purpleBg};
    border: 1px solid ${Colors.purpleBorder};
`;

const OfferCard = styled.div`
    box-sizing: border-box;
    background: ${Colors.white};
    border: 1px solid ${Colors.siteScheduleBorder};
    border-radius: 10px;
    overflow: hidden;
    min-height: 0;
    flex: ${({fillHeight}) => fillHeight ? 'unset' : '0 0 auto'};
    display: ${({fillHeight}) => fillHeight ? 'flex' : 'block'};
    flex-direction: column;
`;

const CardHeader = styled.div`
    padding: 16px 20px 13px;
    border-bottom: 1px solid #f0f0f0;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 13px;
    color: ${Colors.greyText};
    flex: 0 0 auto;
`;

const PayRow = styled(Col)`
    gap: 6px;
    padding: 14px 16px;
    border-radius: 10px;
    border: 1px solid;
    cursor: pointer;
`;

const Badge = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: ${({danger}) => danger ? Colors.badgeRedText : Colors.badgeGreenText};
    background: ${({danger}) => danger ? Colors.badgeRed : Colors.badgeGreen};
    border-radius: 999px;
    padding: 2px 8px;
    flex: 0 0 auto;
    text-align: end;
`;

const EmptyCircle = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: ${Colors.pastDateBg};
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
`;
